import React, { useState } from 'react';
import SocialIcons from '../00-shared/SocialIcons';
import { contactEmail } from '../00-shared/constants';
import { getApiUrl } from '../../src/config/api';

interface ContactFormSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromLight: string;
            toGray: string;
        };
    };
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

function splitName(fullName: string): { firstName: string; lastName: string } {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return { firstName: '', lastName: parts[0] };
    return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
}

const ContactFormSection: React.FC<ContactFormSectionProps> = ({ highlightColor }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        projectType: '',
        message: ''
    });
    const [status, setStatus] = useState<SubmitStatus>('idle');
    const [errorMsg, setErrorMsg] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('submitting');
        setErrorMsg('');

        const { firstName, lastName } = splitName(formData.name);
        if (!lastName) {
            setStatus('error');
            setErrorMsg('Please enter your name.');
            return;
        }

        try {
            const res = await fetch(getApiUrl('/api/contact-form/'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstName,
                    lastName,
                    email: formData.email,
                    projectType: formData.projectType,
                    message: formData.message,
                }),
            });

            const data = await res.json();

            if (res.ok && data.success) {
                setStatus('success');
                setFormData({ name: '', email: '', projectType: '', message: '' });
            } else {
                setStatus('error');
                setErrorMsg(data.error || 'Something went wrong. Please try again.');
            }
        } catch {
            setStatus('error');
            setErrorMsg('Network error. Please try again.');
        }
    };

    return (
        <section className="pt-32 py-20 px-4 sm:px-6 lg:px-8 bg-black relative notebook-grid overflow-hidden">
            <style>{`
                .contact-field::placeholder {
                    color: #6b7280;
                }
                .contact-field option {
                    background-color: #0d0d0d;
                    color: white;
                }
                .contact-field:-webkit-autofill,
                .contact-field:-webkit-autofill:hover,
                .contact-field:-webkit-autofill:focus,
                .contact-field:-webkit-autofill:active {
                    -webkit-box-shadow: 0 0 0 30px #0d0d0d inset !important;
                    -webkit-text-fill-color: white !important;
                    transition: background-color 5000s ease-out 0s;
                    border-color: #2a2a2a !important;
                }
            `}</style>
            <div className="max-w-4xl mx-auto relative z-10">
                <div className="pb-12 border-b border-neutral-800">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-8 text-center observe-fade opacity-0">Contact Us</h1>
                    <p className="text-gray-300 text-center observe-fade opacity-0">
                        We are here to answer any question you may have. Email us at {contactEmail}
                    </p>
                </div>            
                <form onSubmit={handleSubmit} className="space-y-6 observe-fade opacity-0 mt-12">
                    <div>
                        <label htmlFor="name" className="block text-gray-400 text-sm font-medium mb-2">Name</label>
                        <input
                            type="text"
                            id="name"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="contact-field w-full px-4 py-3 text-white focus:outline-none transition-all duration-200"
                            style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a' }}
                            placeholder="Your name"
                            onFocus={(e) => {
                                e.currentTarget.style.backgroundColor = '#1a1a1a';
                                e.currentTarget.style.borderColor = '#e0e0e0';
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.backgroundColor = '#0d0d0d';
                                e.currentTarget.style.borderColor = '#2a2a2a';
                            }}
                        />
                    </div>

                    <div>
                        <label htmlFor="email" className="block text-gray-400 text-sm font-medium mb-2">Email</label>
                        <input
                            type="email"
                            id="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="contact-field w-full px-4 py-3 text-white focus:outline-none transition-all duration-200"
                            style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a' }}
                            placeholder="you@example.com"
                            onFocus={(e) => {
                                e.currentTarget.style.backgroundColor = '#1a1a1a';
                                e.currentTarget.style.borderColor = '#e0e0e0';
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.backgroundColor = '#0d0d0d';
                                e.currentTarget.style.borderColor = '#2a2a2a';
                            }}
                        />
                    </div>

                    <div>
                        <label htmlFor="projectType" className="block text-gray-400 text-sm font-medium mb-2">Your Team</label>
                        <select
                            id="projectType"
                            required
                            value={formData.projectType}
                            onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                            className="contact-field w-full px-4 py-3 focus:outline-none transition-all duration-200"
                            style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a', color: formData.projectType ? 'white' : '#6b7280', appearance: 'none', backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '3rem' }}
                            onFocus={(e) => {
                                e.currentTarget.style.backgroundColor = '#1a1a1a';
                                e.currentTarget.style.borderColor = '#e0e0e0';
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.backgroundColor = '#0d0d0d';
                                e.currentTarget.style.borderColor = '#2a2a2a';
                            }}
                        >
                            <option value="">Select a Team</option>
                            <option value="Ncaa">NCAA</option>
                            <option value="Gym">Gym</option>
                            <option value="Clinic">Clinic</option>
                            <option value="Personal">Personal</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    <div>
                        <label htmlFor="message" className="block text-gray-400 text-sm font-medium mb-2">Your message (optional)</label>
                        <textarea
                            id="message"
                            rows={6}
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            className="contact-field w-full px-4 py-3 text-white focus:outline-none transition-all duration-200 resize-none"
                            style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a' }}
                            placeholder="Tell us about your team or organization..."
                            onFocus={(e) => {
                                e.currentTarget.style.backgroundColor = '#1a1a1a';
                                e.currentTarget.style.borderColor = '#e0e0e0';
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.backgroundColor = '#0d0d0d';
                                e.currentTarget.style.borderColor = '#2a2a2a';
                            }}
                        />
                    </div>

                    {status === 'error' && (
                        <p className="text-red-400 text-sm">{errorMsg}</p>
                    )}

                    {status === 'success' && (
                        <p className="text-green-400 text-sm">Thank you! We'll be in touch soon.</p>
                    )}

                    <button
                        type="submit"
                        disabled={status === 'submitting'}
                        className="w-full px-8 py-4 text-sm font-semibold transition-all duration-200 ease-out cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                            backgroundColor: 'transparent',
                            color: 'white',
                            border: '1px solid white',
                            borderRadius: '0',
                            boxShadow: 'none'
                        }}
                        onMouseEnter={(e) => {
                            if (status !== 'submitting') {
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.color = 'black';
                                e.currentTarget.style.boxShadow = '0 2px 10px -1px rgba(0, 0, 0, 0.1), 0 2px 8px -1px rgba(0, 0, 0, 0.06)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        {status === 'submitting' ? 'SENDING...' : 'SEND MESSAGE'}
                    </button>
                </form>
                <div className="mt-16 flex justify-center">
                    <SocialIcons />
                </div>
            </div>
        </section>
    );
};

export default ContactFormSection;

